import {
	ItemSelectionKind,
	SelectableItemOptions,
	useLongTouch
} from "client/Hooks";
import { Coordinate } from "client/Misc/commonTypes";
import {
	MouseEvent,
	ReactElement,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	DocumentNameKind,
	DocumentNameMode,
	getNameAndThumbnail
} from "./DocumentName";
import {
	GridItemMouseEvent,
	GridView,
	GridViewItemContainerContext,
	Icon,
	IconValue
} from "client/Components";
import { generateTrackingId, isNil } from "client/Misc/utils";
import { ActionAndOthers } from "client_legacy/ndaw/actions";
import { Guid } from "client_legacy/ndaw/guid";
import { GridItemCheckbox } from "client/Components/GridItem/GridItemContent";
import styles from "./DocumentGridViewItem.module.less";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";

export type DocumentGridViewItemProps<TActionContext> = {
	id: number | Guid | string;
	name: string;
	isTouch: boolean;
	isMobile: boolean;
	documentKind: DocumentItemType;
	selectable: SelectableItemOptions;
	selectionActions: readonly ActionAndOthers<TActionContext>[];
	actionContext: TActionContext;
	isInSelectMode: boolean;
	isPreview?: boolean;
	previewUrl?: string;
	isBookmarked?: boolean;
	isInfected?: boolean;
	additionalInfo?: string | ReactElement;
	onEnableSelectMode?(): void;
	onContextMenu(coordinates: Coordinate): void;
	onOpenItem?(): void;
};

export const DocumentGridViewItem = <TActionContext, >(props: DocumentGridViewItemProps<TActionContext>): ReactElement => {
	const {
		id,
		name,
		documentKind,
		previewUrl,
		isPreview = false,
		selectionActions,
		actionContext,
		isTouch,
		isBookmarked,
		isInfected,
		selectable,
		additionalInfo,
		isInSelectMode,
		isMobile,
		onContextMenu,
		onEnableSelectMode,
		onOpenItem
	} = props;

	const [hasImageResult, setHasImageResult] = useState<boolean | null>(null);

	const mode: DocumentNameMode = {
		kind: DocumentNameKind.Default,
		hasBookmark: isBookmarked,
		isInfected: isInfected
	};

	const {
		thumbnail
	} = getNameAndThumbnail(
		name,
		documentKind,
		mode
	);

	let isSelected: boolean;
	let onSelect: ((selectionKind: ItemSelectionKind) => void) | undefined;

	if (selectable === false) {
		isSelected = false;
		onSelect = undefined;
	} else {
		isSelected = selectable.isSelected;
		onSelect = selectable.onSelect;
	}

	const {
		doesOverlapWithLasso,
		hasLassoRect,
		onKeyDown,
		ref,
		tabIndex
	} = useContext(GridViewItemContainerContext);

	const icons = useMemo(
		(): readonly IconValue[] => {
			const result: IconValue[] = [];

			if (isInfected) {
				result.push({
					kind: "font",
					name: "shield-xmark",
					color: "danger"
				});
			}

			if (isBookmarked) {
				result.push({
					kind: "font",
					name: "star-filled",
					color: "bookmark"
				});
			}

			return result;
		},
		[
			isBookmarked,
			isInfected
		]
	);

	const longTouchHandler = useCallback(
		(): void => {
			if (selectable) {
				selectable.onSelect(ItemSelectionKind.Override);
				onEnableSelectMode?.();
			}
		},
		[onEnableSelectMode, selectable]
	);

	const {
		onTouchStart,
		onTouchMove,
		onTouchEnd,
		isActive: isLongTouchActive
	} = useLongTouch({ handler: longTouchHandler });

	const contextMenuHandler = useCallback(
		(args: MouseEvent): void => {
			if (onSelect && !isSelected) {
				onSelect(getSelectionKindFromClickEvent({
					modifiers: {
						shift: args.shiftKey,
						ctrl: args.ctrlKey
					}
				}));
			}

			args.preventDefault();
			args.stopPropagation();
			onContextMenu({
				x: args.clientX,
				y: args.clientY
			});
		},
		[
			isSelected,
			onContextMenu,
			onSelect
		]
	);

	const clickHandler = useCallback(
		(args: GridItemMouseEvent): boolean => {
			if (!isMobile) {
				if (onSelect) {
					onSelect(getSelectionKindFromClickEvent(args));
				}
				return false;
			}

			if (isLongTouchActive) {
				return true;
			}

			if (!isInSelectMode) {
				onOpenItem?.();
			} else {
				onSelect?.(ItemSelectionKind.Append);
			}

			return false;
		},
		[
			isLongTouchActive,
			isInSelectMode,
			isMobile,
			onOpenItem,
			onSelect
		]
	);

	const checkbox = useMemo(
		(): GridItemCheckbox | undefined => {
			if (!selectable || (isMobile && !isInSelectMode)) {
				return undefined;
			}

			return {
				isChecked: isSelected,
				onIsCheckChange: () => selectable.onSelect(ItemSelectionKind.Append)
			};
		},
		[
			isSelected,
			selectable,
			isMobile,
			isInSelectMode
		]
	);

	useEffect(
		() => {
			if (onSelect && hasLassoRect) {
				onSelect(
					doesOverlapWithLasso
						? ItemSelectionKind.AddToScope
						: ItemSelectionKind.RemoveFromScope
				);
			}
		},
		[
			doesOverlapWithLasso,
			hasLassoRect,
			onSelect
		]
	);

	const renderPreviewThumbnail = (): ReactElement => {
		if (previewUrl && (hasImageResult || isNil(hasImageResult))) {
			return (
				<img
					className={styles.image}
					src={previewUrl}
					onLoad={() => setHasImageResult(true)}
					onError={() => setHasImageResult(false)}
				/>
			);
		}

		return (
			<div className={styles.thumbnail}>
				<Icon
					value={{ kind: "svg", name: thumbnail }}
				/>
			</div>
		);
	};

	const openItemHandler: (() => void) | undefined = !isMobile
		? onOpenItem
		: undefined;

	return (
		<GridView.Item
			trackingId={generateTrackingId("grid-view-item", `_${id}`)}
			onContextMenu={contextMenuHandler}
			isActive={isSelected}
			onClick={clickHandler}
			onDoubleClick={openItemHandler}
			onTouchStart={onTouchStart}
			onTouchEnd={onTouchEnd}
			onTouchMove={onTouchMove}
			isTouch={isTouch}
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={tabIndex}
		>
			{isPreview
				? (
					<GridView.Item.PreviewContent
						name={name}
						checkbox={checkbox}
						icons={icons}
						info={additionalInfo}
						onTitleClick={openItemHandler}
						thumbnail={renderPreviewThumbnail()}
						actionContext={actionContext}
						actions={selectionActions}
						hasImage={hasImageResult === true}
					/>
				): (
					<GridView.Item.Content
						name={name}
						checkbox={checkbox}
						icons={icons}
						onTitleClick={openItemHandler}
						actionContext={actionContext}
						actions={selectionActions}
						thumbnail={
							<Icon
								value={{ kind: "svg", name: thumbnail }}
							/>
						}
					/>
				)}
		</GridView.Item>
	);
};

DocumentGridViewItem.displayName = "DocumentGridViewItem";

function getSelectionKindFromClickEvent(event: GridItemMouseEvent): ItemSelectionKind {
	const { modifiers } = event;

	return (
		modifiers.ctrl ? ItemSelectionKind.Append :
		modifiers.shift ? ItemSelectionKind.RangeFromPrevious :
		ItemSelectionKind.Override
	);
}